import { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { getSessionId, generateUUID } from '../lib/session.js'
import { sendChat as apiSendChat, sendFeedback as apiSendFeedback } from '../lib/api.js'
import { getSuggestions, getHeadline } from '../lib/suggestions.js'

const STORAGE_KEY = 'chat.state'

const ChatContext = createContext(null)

function getInitialOpenState() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'open'
  } catch {
    return false
  }
}

const initialState = {
  sessionId: getSessionId(),
  messages: [],
  isOpen: getInitialOpenState(),
  isLoading: false,
  pageContext: '/',
  error: null,
  activeForm: null,
  navAlwaysAllow: false,
  navDecidedIds: [],
  navAutoNavigatedIds: [],
}

function chatReducer(state, action) {
  switch (action.type) {
    case 'OPEN':
      return { ...state, isOpen: true, error: null, activeForm: null }
    case 'CLOSE':
      return { ...state, isOpen: false }
    case 'SET_PAGE':
      return { ...state, pageContext: action.payload }
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload], error: null }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    case 'SHOW_FORM':
      return { ...state, activeForm: action.payload }
    case 'HIDE_FORM':
      return { ...state, activeForm: null }
    case 'SET_NAV_ALWAYS':
      return { ...state, navAlwaysAllow: action.payload }
    case 'MARK_NAV_DECIDED':
      return { ...state, navDecidedIds: [...state.navDecidedIds, action.payload] }
    case 'MARK_NAV_AUTO_NAVIGATED':
      return { ...state, navAutoNavigatedIds: [...state.navAutoNavigatedIds, action.payload] }
    case 'UPDATE_LAST_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((m, i) =>
          i === state.messages.length - 1 ? { ...m, ...action.payload } : m
        ),
      }
    default:
      return state
  }
}

export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState)

  // Persist open/close state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, state.isOpen ? 'open' : 'bubble')
    } catch {
      // ignore
    }
  }, [state.isOpen])

  const openChat = useCallback(() => dispatch({ type: 'OPEN' }), [])
  const closeChat = useCallback(() => dispatch({ type: 'CLOSE' }), [])
  const hideForm = useCallback(() => dispatch({ type: 'HIDE_FORM' }), [])
  const setPageContext = useCallback((path) => dispatch({ type: 'SET_PAGE', payload: path }), [])
  const setNavAlwaysAllow = useCallback((val) => dispatch({ type: 'SET_NAV_ALWAYS', payload: val }), [])
  const markNavDecided = useCallback((id) => dispatch({ type: 'MARK_NAV_DECIDED', payload: id }), [])
  const markNavAutoNavigated = useCallback((id) => dispatch({ type: 'MARK_NAV_AUTO_NAVIGATED', payload: id }), [])

  const sendMessage = useCallback(async (content) => {
    const userMsg = {
      id: generateUUID(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    }
    dispatch({ type: 'ADD_MESSAGE', payload: userMsg })
    dispatch({ type: 'SET_LOADING', payload: true })

    try {
      const result = await apiSendChat({
        session_id: state.sessionId,
        message: content,
        page_url: state.pageContext,
      })

      const assistantMsg = {
        id: generateUUID(),
        role: 'assistant',
        content: result.response,
        timestamp: new Date().toISOString(),
        route: result.route,
        form: result.form,
        returned_path: result.returned_path,
        returned_slug: result.returned_slug,
      }
      dispatch({ type: 'ADD_MESSAGE', payload: assistantMsg })

      if (result.form) {
        dispatch({ type: 'SHOW_FORM', payload: result.form })
      }
    } catch (err) {
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          id: generateUUID(),
          role: 'system',
          content: 'Something went wrong. Try again or email jc.mugunth@gmail.com',
          timestamp: new Date().toISOString(),
        },
      })
      dispatch({ type: 'SET_ERROR', payload: err.message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [state.sessionId, state.pageContext])

  const submitFeedback = useCallback(async (aiResponseId, thumb, correctionText = '') => {
    try {
      await apiSendFeedback({
        session_id: state.sessionId,
        ai_response_id: aiResponseId,
        thumb,
        correction_text: correctionText,
      })
    } catch (err) {
      console.error('Failed to submit feedback:', err)
    }
  }, [state.sessionId])

  const submitForm = useCallback(async (formType, data) => {
    dispatch({ type: 'HIDE_FORM' })
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const result = await apiSendChat({
        session_id: state.sessionId,
        is_form_submit: true,
        form_type: formType,
        ...data,
      })
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          id: generateUUID(),
          role: 'assistant',
          content: result.response,
          timestamp: new Date().toISOString(),
          route: result.route,
          form: result.form,
        },
      })
    } catch (err) {
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          id: generateUUID(),
          role: 'system',
          content: 'Something went wrong submitting the form. Try again or email jc.mugunth@gmail.com',
          timestamp: new Date().toISOString(),
        },
      })
      dispatch({ type: 'SET_ERROR', payload: err.message })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [state.sessionId])

  const suggestions = getSuggestions(state.pageContext)
  const headline = getHeadline(state.pageContext)

  const value = {
    ...state,
    openChat,
    closeChat,
    hideForm,
    setPageContext,
    sendMessage,
    submitFeedback,
    submitForm,
    suggestions,
    headline,
    setNavAlwaysAllow,
    markNavDecided,
    markNavAutoNavigated,
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}

export function useChatContext() {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider')
  return ctx
}
